/**
 * client/src/lib/use-recovery-push.ts
 *
 * Guardian-approval WebSocket push listener (PR 1, Task 3, refs #2257) —
 * the in-app half of telling a guardian a ward's recovery session needs
 * their signature RIGHT NOW, not only if they happen to poll Invitations.
 *
 * Listens for `recovery:guardian_request`. Modelled directly on
 * `use-handshake-push.ts` (Reuse-First Check, DEVELOPMENT/quality.md Code
 * Review Criteria #8): the same `{type:"auth", userId, sessionId}`
 * handshake and readiness-gate contract.
 *
 * REACH IS NOT GUARANTEED — a WebSocket notice reaches a LIVE session only.
 * The 60s poll on `GET /api/recovery/guardian/pending` (AC-5) is the
 * load-bearing discovery path; this listener is defence in depth over it.
 * No copy anywhere may present this push as the delivery mechanism.
 *
 * This is a notice of an already-committed recovery-guardian request only —
 * never proof that anyone is a live human (Friction Rule clause 2).
 */

import { useCurrentUser } from "@/lib/use-current-user";
import { useReconnectingWs, type UseReconnectingWsResult } from "@/lib/use-reconnecting-ws";

export interface RecoveryGuardianRequestFrame extends Record<string, unknown> {
  type: "recovery:guardian_request";
  sessionId: string;
  protectedUserId: string;
  connectionId: string;
  expiresAt: string;
}

/**
 * Listens for `recovery:guardian_request` pushes addressed to the current
 * authenticated user. A null/loading current user opens no socket at all
 * (readiness-gate contract, same as `useHandshakePush`).
 */
export function useRecoveryPush(
  onEvent: (frame: RecoveryGuardianRequestFrame) => void
): UseReconnectingWsResult {
  const { user } = useCurrentUser();
  return useReconnectingWs<RecoveryGuardianRequestFrame>(
    () => (user?.id ? { type: "auth", userId: user.id, sessionId: `recovery-push-${user.id}` } : null),
    (msg) => msg.type === "recovery:guardian_request",
    onEvent,
    user?.id
  );
}
